'use client'

import { Typography } from "@/shared/ui/atoms"
import { CustomButton } from "@/shared/ui/molecules"
import { Icon } from "@/shared/ui/atoms"
import Image from "next/image"
import Link from "next/link"
import { TreatmentCaseDetailResponse } from "@/shared/types/type/treatment"
import { DirectionIcon, IconButton } from "@/shared/ui/atoms"
import { useParams, useRouter } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { useMutationTreatmentCaseHelpful, useMutationTreatmentCaseHelpfulDelete } from "@/entities/treatment-case/hooks/useTreatment"
import { registerTreatmentCaseViewClient } from "@/entities/treatment-case/api/treatment-case.client"
import { Loading } from "@/shared/ui/organisms"
import { useLocale, useTranslations } from "next-intl"
import { useAuthContext } from "@/app/providers/AuthProviders"
import { toTreatmentCaseBodyHtml } from "@/entities/treatment-case/lib/seo"
import { toIsoSeoDate } from "@/shared/lib/seo-date"
import { buildHospitalShortPath } from "@/entities/hospital/lib/detail-route"

interface TreatmentCaseDetailTemplatesProps {
  detail: TreatmentCaseDetailResponse
}

const TreatmentCaseDetailTemplates = ({ detail }: TreatmentCaseDetailTemplatesProps) => {
  const t = useTranslations()
  const locale = useLocale()
  const router = useRouter()
  const { id } = useParams() as { id: string }
  const { loggedIn } = useAuthContext()
  const { mutate } = useMutationTreatmentCaseHelpful()
  const { mutate: deleteHelpful } = useMutationTreatmentCaseHelpfulDelete()
  const { treatmentSubjectName, title, createdAt, viewCount, hospitalOriginId, hospitalOriginName, hospitalFullAddress, hospitalThumbnailUrl, content, isHelpful } = detail
  const [isDisplayHelpfulButton, setIsDisplayHelpfulButton] = useState(isHelpful)
  const [isLoading, setIsLoading] = useState(false)
  const machineReadableCreatedAt = toIsoSeoDate(createdAt)

  // 조회 등록: 마운트 1회만 발화(ref 가드 — StrictMode 이중 실행도 백엔드 유니크 인덱스가 흡수).
  // 상세 데이터는 서버 캐시로 오므로(revalidate) 조회수는 이 클라이언트 경로가 정본이다.
  const viewRegisteredRef = useRef(false)
  useEffect(() => {
    if (viewRegisteredRef.current) return
    viewRegisteredRef.current = true
    registerTreatmentCaseViewClient(Number(id)).catch(() => {})
  }, [id])

  const handleHospitalInfoClick = () => {
    setTimeout(() => {
      setIsLoading(true)
    }, 100)
  }

  const handleHelpful = () => {
    if(!loggedIn) return router.push(`/${locale}/sign-in?redirect=/${locale}/treatment-case/${id}`)
    if(isDisplayHelpfulButton) {
      deleteHelpful(Number(id))
    } else {
      mutate(Number(id))
    }
    setIsDisplayHelpfulButton(!isDisplayHelpfulButton)
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <section className="flex-1 flex flex-col bg-white border-t border-gray-100">
      <div className="flex flex-col justify-center px-4 pt-6 pb-2">
        <Typography size={14} lineHeight={22} className="text-primary-600 font-medium">{treatmentSubjectName}</Typography>
        <Typography as="h1" size={20} lineHeight={28} className="mt-1 text-gray-900 font-semibold">{title}</Typography>
        <div className="mt-2.5 flex items-center gap-1.5">
          <time
            dateTime={machineReadableCreatedAt}
            className="text-[14px] leading-[22px] tracking-[-0.28px] text-gray-400"
          >
            {createdAt}
          </time>
          <span className="w-[2px] h-[2px] bg-gray-200 rounded-full my-auto" />
          <Typography size={14} lineHeight={22} className="text-gray-400 tracking-[-0.28px]">조회수 {viewCount}</Typography>
        </div>
        <Link
          href={buildHospitalShortPath(locale, hospitalOriginId)}
          onClick={handleHospitalInfoClick}
          className="mt-5 flex items-center justify-between w-full py-2 px-4 rounded-lg bg-gray-100"
        >
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0 relative w-8 h-8 overflow-hidden rounded-full bg-gray-200">
              <Image src={hospitalThumbnailUrl} alt="병원 로고" fill sizes="32px" className="object-cover" />
            </div>
            <div>
              <Typography size={16} lineHeight={22} className="text-gray-800 font-semibold text-start">{hospitalOriginName}</Typography>
              <Typography size={14} lineHeight={18} className="text-gray-600 text-start">{hospitalFullAddress}</Typography>
            </div>
          </div>
          <DirectionIcon size={20} className="text-gray-600" rotate={270} strokeWidth="1.5"/>
        </Link>
        <span className="w-full h-[1px] bg-gray-100 my-2" />
      </div>
      <div
        id="medicontent"
        dangerouslySetInnerHTML={{ __html: toTreatmentCaseBodyHtml(content) }}
      />
      <div className="flex items-center justify-between px-5 pt-[14px] pb-[18px] bg-white border-t border-gray-100">
        <CustomButton className="flex items-center gap-1 py-1.5 px-3 border border-gray-200 rounded-full" onClick={handleHelpful}>
          <Typography size={14} lineHeight={22} className="text-gray-600 font-medium">{t("helpful")}</Typography>
          <Icon
            src={`/icons/thumbs-up${isDisplayHelpfulButton ? "-fill" : ""}.png`}
            width={16}
            height={16}
            alt="thumb"
            className={isDisplayHelpfulButton ? "animate-pop-150" : ""}
          />
        </CustomButton>
        <IconButton onClick={scrollToTop}>
          <DirectionIcon size={24} className="text-gray-600" rotate={180} />
        </IconButton>
      </div>
      {isLoading && <Loading />}
    </section>
  )
}

export default TreatmentCaseDetailTemplates
